import {useMemo, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {Card} from 'shells/card/card';
import {Stack} from 'shells/layout/stack/stack';
import {UpgradeArrow} from 'shells/limits/upgrade-arrow';
import {Link} from 'shells/link/link';
import {Switch} from 'shells/switch/switch';
import {Table} from 'shells/table/table';
import {Body} from 'shells/typography/body';

import type {AiFeatureType, AiFeatureCategory} from 'nutshell-graphql-types';
import type {GetAiFeatureUsage_aiFeatureUsage_features as AiFeature} from 'nutshell-graphql-types';

import {AiFeatureGateModal} from '../../ai-home/ai-feature-gate-modal/ai-feature-gate-modal';
import {AI_FEATURES_CONFIG_MAP} from '../../ai-home/features/features-list';

import type {FeatureGroup, FeatureGroupRow} from './feature-config';
import {groupFeatures} from './helpers';
import {UsageStats} from './usage-stats';

interface Props {
    title: string;
    subtitle?: string;
    currentMonthUsage: number;
    currentMonthLimit: number | null;
    lastMonthUsage: number;
    features: AiFeature[];
    featureGroups: FeatureGroup[];
    featureColumnHeader: string;
    category: AiFeatureCategory;
    onToggle: (
        settingName: string,
        newEnabled: boolean,
        types: AiFeatureType[],
        category: AiFeatureCategory
    ) => void;
}

export function AiFeatureUsageSection(props: Props) {
    const {features, featureGroups, featureColumnHeader, category, onToggle, currentMonthLimit} =
        props;
    const isUnlimited = currentMonthLimit === null;
    const rows = useMemo(() => groupFeatures(features, featureGroups), [features, featureGroups]);
    const [showFeatureGateModal, setShowFeatureGateModal] = useState<boolean>(false);
    const [aiFeatureType, setAiFeatureType] = useState<AiFeatureType | undefined>(undefined);
    const [featureDisplayName, setFeatureDisplayName] = useState<string | undefined>(undefined);

    const columns = useMemo(
        () => [
            {
                id: 'isEnabled',
                Header: 'Enabled',
                accessor: (row: FeatureGroupRow) => row,
                width: 80,
                minWidth: 80,
                maxWidth: 80,
                dataType: 'component' as const,
                disableSortBy: true,
                Cell: ({cell}: {cell: {value: FeatureGroupRow}}) => {
                    if (!cell.value.canDisableGlobally) {
                        return null;
                    }

                    return (
                        <Switch
                            isOn={cell.value.requiresUpgrade ? false : cell.value.isEnabled}
                            onChange={() => {
                                if (cell.value.settingName) {
                                    onToggle(
                                        cell.value.settingName,
                                        !cell.value.isEnabled,
                                        cell.value.types,
                                        category
                                    );
                                }
                            }}
                            isRounded={true}
                            isSmall={true}
                            isDisabled={cell.value.requiresUpgrade || !cell.value.settingName}
                        />
                    );
                },
            },
            {
                id: 'feature',
                Header: featureColumnHeader,
                accessor: (row: FeatureGroupRow) => row,
                minWidth: 400,
                dataType: 'component' as const,
                disableSortBy: true,
                Cell: ({cell}: {cell: {value: FeatureGroupRow}}) => {
                    // There could be multiple, we just go with the first one for simplicity
                    const featureType = cell.value.types[0];
                    const featureConfig = featureType
                        ? AI_FEATURES_CONFIG_MAP.get(featureType)
                        : undefined;
                    const featureLink = featureConfig
                        ? `/ai/home/${featureConfig.pathSegment}`
                        : undefined;

                    return (
                        <Stack spacing={2}>
                            <Stack isHorizontal={true} spacing={4} alignItems='center'>
                                {featureLink ? (
                                    // @ts-expect-error
                                    <Link to={featureLink} as={RouterLink} size='small'>
                                        {cell.value.displayName}
                                    </Link>
                                ) : (
                                    <Body color='dark' size='small'>
                                        {cell.value.displayName}
                                    </Body>
                                )}
                                {cell.value.requiresUpgrade && (
                                    <UpgradeArrow
                                        featureName={cell.value.displayName}
                                        onClick={() => {
                                            setAiFeatureType(cell.value.types[0]);
                                            setFeatureDisplayName(cell.value.displayName);
                                            setShowFeatureGateModal(true);
                                        }}
                                    />
                                )}
                            </Stack>
                            <Body color='light' size='small'>
                                {cell.value.description}
                            </Body>
                        </Stack>
                    );
                },
            },
            {
                id: 'usage',
                Header: isUnlimited ? 'Usage' : 'Usage this month',
                accessor: (row: FeatureGroupRow) => row,
                width: 130,
                minWidth: 130,
                maxWidth: 130,
                rightAlign: true,
                dataType: 'component' as const,
                disableSortBy: true,
                Cell: ({cell}: {cell: {value: FeatureGroupRow}}) => {
                    if (cell.value.requiresUpgrade) {
                        return (
                            <Body color='light' size='small'>
                                Not in your plan
                            </Body>
                        );
                    }

                    if (isUnlimited) {
                        return (
                            <Stack spacing={0} alignItems='end'>
                                <Body color='dark' size='small'>
                                    {cell.value.totalUsage.toLocaleString()} / ∞
                                </Body>
                                <Body color='light' size='small'>
                                    Included in your plan
                                </Body>
                            </Stack>
                        );
                    }

                    return (
                        <Body color='dark' size='small'>
                            {cell.value.totalUsage.toLocaleString()}
                        </Body>
                    );
                },
            },
        ],
        [featureColumnHeader, isUnlimited, onToggle, category]
    );

    return (
        <Stack spacing={12}>
            <Stack spacing={4}>
                <Body color='dark' size='large' isBold={true}>
                    {props.title}
                </Body>
                {props.subtitle && (
                    <Body color='light' size='small'>
                        {props.subtitle}
                    </Body>
                )}
            </Stack>
            <UsageStats
                currentMonthUsage={props.currentMonthUsage}
                currentMonthLimit={props.currentMonthLimit}
                lastMonthUsage={props.lastMonthUsage}
            />
            <Card hasBorder={true} fitContent={false} fitHeight={true}>
                <Table
                    columns={columns}
                    data={rows}
                    disableSorting={true}
                    disableFilters={true}
                    isResizable={false}
                    headerBackgroundColor='offWhiteDk'
                    isCompact={true}
                    stickyHeaders={false}
                    hasBorders={true}
                    isCheckboxSelectable={false}
                    fullWidth={true}
                    shouldShowFooter={false}
                />
            </Card>
            <AiFeatureGateModal
                isOpen={showFeatureGateModal}
                onClose={() => setShowFeatureGateModal(false)}
                reason='notInPlan'
                featureName={featureDisplayName}
                aiFeatureType={aiFeatureType}
            />
        </Stack>
    );
}
